import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, FormField, PasswordInput } from '@/components';
import { resetPassword } from '../../api';
import {
  AUTH_BUTTON_CLASS,
  AUTH_FORM_CARD_CLASS,
  AUTH_INPUT_CLASS,
  AUTH_LABEL_CLASS,
} from '../../lib/authStyles';

export function ResetPasswordForm() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const isCompleted =
      sessionStorage.getItem('reset_flow_completed') === 'true';
    if (!location.state || !location.state.email || isCompleted) {
      navigate('/login', { replace: true });
    }
  }, [location.state, navigate]);

  const email = location.state?.email || '';
  const otp_code = location.state?.otp_code || '';
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});

  const { mutate, isPending } = useMutation({
    mutationFn: resetPassword,
    onSuccess: (data) => {
      toast.success(
        data?.message || 'Password reset successful! Redirecting to login...',
      );
      sessionStorage.setItem('reset_flow_completed', 'true');
      setTimeout(() => {
        navigate('/login', { replace: true, state: null });
      }, 2000);
    },
    onError: (err) => {
      const errorData = err.response?.data;
      if (
        errorData &&
        typeof errorData === 'object' &&
        !Array.isArray(errorData) &&
        !errorData.message &&
        !errorData.detail
      ) {
        const newErrors = {};
        Object.keys(errorData).forEach((key) => {
          let fieldName = key;
          if (key === 'password') fieldName = 'password';
          if (key === 'confirm_password') fieldName = 'confirmPassword';

          const errMsg = Array.isArray(errorData[key])
            ? errorData[key][0]
            : errorData[key];

          if (key === 'non_field_errors') {
            toast.error(errMsg);
          } else {
            newErrors[fieldName] = errMsg;
          }
        });
        setFieldErrors(newErrors);
      } else if (Array.isArray(errorData) && errorData.length > 0) {
        toast.error(errorData[0]);
      } else {
        toast.error(
          errorData?.message ||
            errorData?.detail ||
            'Failed to reset password. Please try again.',
        );
      }
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (fieldErrors[name]) setFieldErrors({ ...fieldErrors, [name]: '' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.password || !formData.confirmPassword) {
      setFieldErrors({ confirmPassword: 'Please fill in all fields.' });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setFieldErrors({ confirmPassword: 'Passwords do not match.' });
      return;
    }

    if (formData.password.length < 6) {
      setFieldErrors({ password: 'Password must be at least 6 characters.' });
      return;
    }

    mutate({
      email,
      otp_code,
      password: formData.password,
      confirmPassword: formData.confirmPassword,
    });
  };

  if (!location.state || !location.state.email) return null;

  return (
    <div className={`${AUTH_FORM_CARD_CLASS} text-left`}>
      <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4">
        <FormField
          label="New Password"
          htmlFor="password"
          error={fieldErrors.password}
          labelClassName={AUTH_LABEL_CLASS}
        >
          <PasswordInput
            size="lg"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Enter your new password"
            error={!!fieldErrors.password}
            className={AUTH_INPUT_CLASS}
            disabled={isPending}
          />
        </FormField>

        <FormField
          label="Confirm Password"
          htmlFor="confirmPassword"
          error={fieldErrors.confirmPassword}
          labelClassName={AUTH_LABEL_CLASS}
        >
          <PasswordInput
            size="lg"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Enter your confirm password"
            error={!!fieldErrors.confirmPassword}
            className={AUTH_INPUT_CLASS}
            disabled={isPending}
          />
        </FormField>

        {/* Submit Button */}
        <div className="flex justify-center mt-6">
          <Button
            size="lg"
            type="submit"
            variant="primary"
            className={`${AUTH_BUTTON_CLASS} w-[200px] h-[48px]`}
            disabled={isPending}
          >
            {isPending ? (
              <span className="flex items-center justify-center w-full">
                <svg
                  className="animate-spin h-5 w-5 text-surface-mint"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </span>
            ) : (
              <>
                <span>Reset Password</span>
                <span>→</span>
              </>
            )}
          </Button>
        </div>

        {/* Back to Login */}
        <div className="text-center mt-2">
          <button
            type="button"
            onClick={() => {
              localStorage.clear();
              window.location.href = '/login';
            }}
            className="text-body-lg font-normal font-sf-pro-display text-text-primary hover:text-primary transition-colors cursor-pointer inline-flex items-center gap-1.5"
          >
            <svg
              className="w-4 h-4 inline"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5L8.25 12l7.5-7.5"
              />
            </svg>
            <span>Back to Login</span>
          </button>
        </div>
      </form>
    </div>
  );
}
